import {
  CheckCircle2Icon,
  CheckIcon,
  InfoIcon,
  Loader2Icon,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import Logo from "../../components/Logo";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { selectUser } from "../login/selectors";
import * as CognitoAuthService from "../../services/CognitoAuthService";

const ISSUER = "Betterfield";

interface UserFromSelector {
  email?: string;
}

/*
 * Cognito-flavoured 3-step setup. The previous flow had a "save recovery
 * codes" step backed by a backend-side bcrypt list; Cognito doesn't expose
 * recovery codes natively (the equivalent is an admin-driven MFA reset),
 * so the step is dropped. If recovery codes are required again, generate
 * them client-side and persist via a follow-up backend usecase.
 */
export default function Setup2FA() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();
  const user = useSelector(selectUser()) as UserFromSelector | null;

  const steps = [
    t("setup-2fa.step-welcome", "Welcome"),
    t("setup-2fa.step-scan-qr", "Scan QR Code"),
    t("setup-2fa.step-verify", "Verify"),
  ];

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState<0 | 1 | 2 | 3>(0);
  const [secret, setSecret] = useState<string | null>(null);
  const [verificationToken, setVerificationToken] = useState("");

  const handleSkip = () => {
    navigate("/dashboard/app");
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      const { secretCode } = await CognitoAuthService.setupTOTP();
      setSecret(secretCode);
      setActiveStep(1);
    } catch (err) {
      showNotification(
        "error",
        (err as { message?: string })?.message ||
          t("2fa.setup-error", "Failed to setup 2FA")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    if (!verificationToken || verificationToken.length !== 6) {
      showNotification(
        "error",
        t("2fa.invalid-token", "Please enter a 6-digit code")
      );
      return;
    }
    setLoading(true);
    try {
      await CognitoAuthService.verifyTOTP(verificationToken);
      setActiveStep(3); // success step (out of stepper range, intentional)
    } catch (err) {
      showNotification(
        "error",
        (err as { message?: string })?.message ||
          t("2fa.enable-error", "Invalid code. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    navigate("/dashboard/app");
  };

  const otpauthUri =
    secret && user?.email
      ? `otpauth://totp/${encodeURIComponent(
          `${ISSUER}:${user.email}`
        )}?secret=${secret}&issuer=${encodeURIComponent(ISSUER)}`
      : null;

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <WelcomeStep
            loading={loading}
            onSkip={handleSkip}
            onSetup={handleSetup}
          />
        );
      case 1:
        return (
          <QrStep
            otpauthUri={otpauthUri}
            secret={secret}
            onSkip={handleSkip}
            onNext={() => setActiveStep(2)}
          />
        );
      case 2:
        return (
          <VerifyStep
            loading={loading}
            verificationToken={verificationToken}
            setVerificationToken={setVerificationToken}
            onEnable={handleEnable}
          />
        );
      case 3:
        return <SuccessStep onFinish={handleFinish} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {t("setup-2fa.welcome-title", "Secure Your Account")} | Betterfield
        </title>
      </Helmet>
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>

        {activeStep < 3 && (
          <>
            <Stepper steps={steps} activeStep={activeStep} className="mb-4" />
            <Separator className="mb-6" />
          </>
        )}

        <div>{renderStep()}</div>
      </main>
    </>
  );
}

// ---------- Stepper -----------------------------------------------------

interface StepperProps {
  steps: string[];
  activeStep: number;
  className?: string;
}

function Stepper({ steps, activeStep, className }: StepperProps) {
  return (
    <ol className={cn("flex items-start", className)}>
      {steps.map((label, index) => {
        const isActive = index === activeStep;
        const isComplete = index < activeStep;
        return (
          <li
            key={label}
            className="relative flex flex-1 flex-col items-center text-center"
          >
            {/* Connector to next step */}
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "absolute top-3.5 left-1/2 -z-10 h-px w-full",
                  index < activeStep ? "bg-primary" : "bg-border"
                )}
                aria-hidden
              />
            )}
            <div
              className={cn(
                "flex size-7 items-center justify-center rounded-full border text-xs font-semibold",
                isComplete &&
                  "border-primary bg-primary text-primary-foreground",
                isActive &&
                  "border-primary bg-background text-primary",
                !isComplete &&
                  !isActive &&
                  "border-border bg-background text-muted-foreground"
              )}
            >
              {isComplete ? <CheckIcon className="size-3.5" /> : index + 1}
            </div>
            <span
              className={cn(
                "mt-2 px-2 text-xs",
                isActive ? "font-medium text-foreground" : "text-muted-foreground"
              )}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// ---------- Step 0: Welcome --------------------------------------------

interface WelcomeStepProps {
  loading: boolean;
  onSkip: () => void;
  onSetup: () => void;
}

function WelcomeStep({ loading, onSkip, onSetup }: WelcomeStepProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h1 className="text-2xl font-bold tracking-tight">
          {t("setup-2fa.welcome-title", "Secure Your Account")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "setup-2fa.welcome-description",
            "Two-factor authentication (2FA) adds an extra layer of security to your account. When you log in, you’ll need to enter a code from your authenticator app in addition to your password."
          )}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("setup-2fa.welcome-benefits", "Benefits of enabling 2FA:")}
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            {t(
              "setup-2fa.benefit-1",
              "Protects your account even if your password is compromised"
            )}
          </li>
          <li>
            {t(
              "setup-2fa.benefit-2",
              "Prevents unauthorized access to your data"
            )}
          </li>
          <li>{t("setup-2fa.benefit-3", "Industry-standard security practice")}</li>
        </ul>
      </div>

      <Alert variant="info">
        <InfoIcon />
        <AlertDescription>
          {t(
            "setup-2fa.optional-notice",
            "Setting up 2FA is optional, but highly recommended for security."
          )}
        </AlertDescription>
      </Alert>

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onSkip}>
          {t("setup-2fa.skip", "Skip for now")}
        </Button>
        <Button className="flex-1" onClick={onSetup} disabled={loading}>
          {loading && <Loader2Icon className="size-4 animate-spin" />}
          {t("setup-2fa.setup-button", "Set up 2FA")}
        </Button>
      </div>
    </div>
  );
}

// ---------- Step 1: QR Code --------------------------------------------

interface QrStepProps {
  otpauthUri: string | null;
  secret: string | null;
  onSkip: () => void;
  onNext: () => void;
}

function QrStep({ otpauthUri, secret, onSkip, onNext }: QrStepProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">
          {t("2fa.scan-qr", "Step 1: Scan QR Code")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t(
            "2fa.scan-description",
            "Use Google Authenticator, Microsoft Authenticator, or any TOTP app to scan this QR code:"
          )}
        </p>
      </div>

      {otpauthUri && (
        <div className="flex justify-center">
          {/* QR codes require a white background regardless of theme —
              the scanning algorithm depends on the high-contrast pattern. */}
          <div className="rounded-md border bg-white p-3">
            <QRCodeSVG value={otpauthUri} size={220} includeMargin={false} />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          {t("2fa.manual-entry", "Or enter this code manually:")}
        </p>
        <Input value={secret ?? ""} readOnly className="font-mono" />
      </div>

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onSkip}>
          {t("setup-2fa.skip", "Skip for now")}
        </Button>
        <Button className="flex-1" onClick={onNext}>
          {t("common.next", "Next")}
        </Button>
      </div>
    </div>
  );
}

// ---------- Step 2: Verify ---------------------------------------------

interface VerifyStepProps {
  loading: boolean;
  verificationToken: string;
  setVerificationToken: (value: string) => void;
  onEnable: () => void;
}

function VerifyStep({
  loading,
  verificationToken,
  setVerificationToken,
  onEnable,
}: VerifyStepProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">
          {t("2fa.verify", "Step 2: Verify")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t(
            "2fa.verify-description",
            "Enter the 6-digit code from your authenticator app:"
          )}
        </p>
      </div>

      <div className="space-y-2">
        <label
          htmlFor="setup-2fa-code"
          className="text-sm font-medium"
        >
          {t("2fa.verification-code", "Verification Code")}
        </label>
        <Input
          id="setup-2fa-code"
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={verificationToken}
          onChange={(e) =>
            setVerificationToken(
              e.target.value.replace(/\D/g, "").slice(0, 6)
            )
          }
          className="text-center text-2xl tracking-[0.5rem]"
        />
        <p className="text-xs text-muted-foreground">
          {t("2fa.verification-help", "Enter the 6-digit code from your app")}
        </p>
      </div>

      <Button
        className="w-full"
        onClick={onEnable}
        disabled={loading || verificationToken.length !== 6}
      >
        {loading && <Loader2Icon className="size-4 animate-spin" />}
        {t("2fa.enable-confirm", "Enable 2FA")}
      </Button>
    </div>
  );
}

// ---------- Step 3: Success --------------------------------------------

interface SuccessStepProps {
  onFinish: () => void;
}

function SuccessStep({ onFinish }: SuccessStepProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <Alert variant="success">
        <CheckCircle2Icon />
        <AlertDescription>
          {t("setup-2fa.success-title", "2FA Enabled Successfully!")}
        </AlertDescription>
      </Alert>
      <p className="text-sm text-muted-foreground">
        {t(
          "setup-2fa.success-description",
          "Your account is now protected with two-factor authentication. You’ll need to enter a code from your authenticator app when logging in."
        )}
      </p>
      <Button className="w-full" onClick={onFinish}>
        {t("setup-2fa.go-to-login", "Continue to Login")}
      </Button>
    </div>
  );
}
